import React, { useEffect, useState } from "react";
import {
  Modal,
  Descriptions,
  Table,
  Button,
  Select,
  Popconfirm,
  message,
  Typography,
  Divider,
  Space,
  Tag,
  Input,
  InputNumber,
} from "antd";
import { PrinterOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { adminRestaurantPosService } from "../../../services/adminService";
import { PAYMENT_METHOD_OPTIONS } from "../../../constants/paymentMethods";
import { canViewPrices } from '../../../utils/permissions';

const { Text } = Typography;


interface OrderDetailsModalProps {
  visible: boolean;
  orderId: string | null;
  onCancel: () => void;
  onUpdate: () => void;
  onPrintReceipt: (orderId: string) => void;
  onPrintKot?: (orderId: string) => void;
  onPrintTaxInvoice?: (orderId: string, bookingId?: string, guestName?: string) => void;
  onPrintA4?: (orderId: string) => void;
  onEditOrder?: (orderData: any) => void;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  visible,
  orderId,
  onCancel,
  onUpdate,
  onPrintReceipt,
  onPrintKot,
  onPrintTaxInvoice,
  onPrintA4,
  onEditOrder,
}) => {
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [paidMethod, setPaidMethod] = useState<string>("cash");
  const [splitLabel, setSplitLabel] = useState("");
  const [splitAmount, setSplitAmount] = useState<number | null>(null);
  const [splitMethod, setSplitMethod] = useState<string>("cash");
  const [splitLoading, setSplitLoading] = useState(false);

  const fmt = (v: any) =>
    `LKR ${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;

  useEffect(() => {
    if (visible && orderId) {
      fetchOrderDetails();
    } else {
      setOrderData(null);
    }
  }, [visible, orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const res = await adminRestaurantPosService.getOrderById(orderId!);
      setOrderData(res.data);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status: string, paymentMethod?: string) => {
    if (!orderId) return;
    try {
      setLoading(true);
      await adminRestaurantPosService.updateOrderStatus(orderId, status, paymentMethod);
      message.success("Status updated");
      fetchOrderDetails();
      onUpdate();
    } catch (error) {
      console.error(error);
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleStatusChange = (status: string) => {
    if (status === "paid") {
      setPaidMethod(orderData?.payment_method || "cash");
      setPaidModalOpen(true);
      return;
    }
    handleUpdateStatus(status);
  };

  const confirmPaidStatus = async () => {
    setPaidModalOpen(false);
    await handleUpdateStatus("paid", paidMethod);
  };

  const handleAddSplit = async () => {
    if (!orderId) return;
    if (!splitLabel.trim()) {
      message.warning("Enter a split label");
      return;
    }
    if (!splitAmount || splitAmount <= 0) {
      message.warning("Enter a valid amount");
      return;
    }
    try {
      setSplitLoading(true);
      await adminRestaurantPosService.createSplitBill(orderId, {
        split_label: splitLabel.trim(),
        amount: Number(splitAmount),
        payment_method: splitMethod,
      });
      message.success("Split bill created");
      setSplitLabel("");
      setSplitAmount(null);
      fetchOrderDetails();
      onUpdate();
    } catch (error) {
      console.error(error);
      message.error("Failed to create split bill");
    } finally {
      setSplitLoading(false);
    }
  };

  const handleSettleSplit = async (splitBillId: string) => {
    try {
      setSplitLoading(true);
      await adminRestaurantPosService.settleSplitBill(splitBillId);
      message.success("Split bill settled");
      fetchOrderDetails();
      onUpdate();
    } catch (error) {
      console.error(error);
      message.error("Failed to settle split bill");
    } finally {
      setSplitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      await adminRestaurantPosService.deleteOrder(orderId);
      message.success("Order deleted successfully");
      onUpdate();
      onCancel();
    } catch (error) {
      console.error(error);
      message.error("Failed to delete order");
      setLoading(false);
    }
  };

  if (!orderData) {
    return (
      <Modal
        open={visible}
        onCancel={onCancel}
        footer={null}
        title="Order Details"
      >
        <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
      </Modal>
    );
  }

  const isRoomService = orderData.order_type === "room_service";
  const maxDays = orderData.items?.length
    ? Math.max(...orderData.items.map((i: any) => Number(i.no_of_days) || 1))
    : 1;
  const startDate = new Date(orderData.created_at);
  const endDate = new Date(startDate);
  if (maxDays > 1) {
    endDate.setDate(endDate.getDate() + maxDays - 1);
  }

  const columns = [
    {
      title: "Menu Item",
      key: "menu_item",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {record.image_url ? (
            <img
              src={record.image_url}
              alt={record.menu_item_name}
              style={{
                width: 40,
                height: 40,
                borderRadius: 6,
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 6,
                backgroundColor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text type="secondary" style={{ fontSize: 10 }}>
                No Img
              </Text>
            </div>
          )}
          <Text strong>{record.menu_item_name}</Text>
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
    },
    ...(orderData?.order_type === "room_service" ? [
      {
        title: "Guests",
        dataIndex: "no_of_guests",
        key: "no_of_guests",
        align: "center" as const,
      },
      {
        title: "Days",
        dataIndex: "no_of_days",
        key: "no_of_days",
        align: "center" as const,
      }
    ] : []),
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right" as const,
      render: (val: any) =>
        !canViewPrices() ? "-" :
        val
          ? `LKR ${Number(val).toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}`
          : "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
    },
  ];

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={null}
      centered
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: 24 }}>
        {/* Title on its own row, actions on a row below — the six action
            buttons no longer compete with the title for the same line. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <Typography.Title
            level={4}
            style={{ margin: 0, alignSelf: "flex-start" }}
          >
            Order {orderData.order_number}
          </Typography.Title>
          <Space
            wrap
            style={{ alignSelf: "flex-end", maxWidth: "100%", rowGap: 8 }}
          >
            {onEditOrder && (
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={() => {
                  onEditOrder(orderData);
                  onCancel();
                }}
              >
                Edit
              </Button>
            )}
            <Popconfirm
              title="Print Receipt?"
              description="This will send the receipt to the printer."
              onConfirm={() => onPrintReceipt(orderId!)}
              okText="Print"
            >
              <Button
                type="primary"
                icon={<PrinterOutlined />}
              >
                Receipt
              </Button>
            </Popconfirm>
            {onPrintTaxInvoice && (
              <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => onPrintTaxInvoice(orderId!, orderData?.booking_id, orderData?.guest_name)}
              >
                Tax Invoice
              </Button>
            )}
            {onPrintKot && (
              <Popconfirm
                title="Print KOT?"
                description="This will send the KOT to the kitchen printer."
                onConfirm={() => onPrintKot(orderId!)}
                okText="Print"
              >
                <Button
                  type="default"
                  icon={<PrinterOutlined />}
                >
                  Print KOT
                </Button>
              </Popconfirm>
            )}
            {onPrintA4 && (
              <Popconfirm
                title="Print A4 Receipt?"
                description="This will open the receipt in a new tab."
                onConfirm={() => onPrintA4(orderId!)}
                okText="Print"
              >
                <Button
                  type="default"
                  icon={<PrinterOutlined />}
                >
                  A4 Receipt
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Delete Order"
              description="Are you sure you want to delete this order? This action cannot be undone."
              onConfirm={handleDelete}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </div>

        <Descriptions
          bordered
          column={2}
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="Order Type">
            <Tag
              color={
                orderData.order_type === "room_service" ? "blue" : "orange"
              }
            >
              {orderData.order_type === "room_service"
                ? "Room Service"
                : "Dine In"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Select
              size="small"
              value={orderData.status}
              style={{ width: 140 }}
              onChange={handleStatusChange}
              options={[
                { value: "kot_pending", label: "KOT Pending" },
                { value: "preparing", label: "Preparing" },
                { value: "served", label: "Served" },
                { value: "billed", label: "Billed" },
                { value: "paid", label: "Paid" },
                { value: "cancelled", label: "Cancelled" },
              ]}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Table/Room">
            {orderData.order_type === "room_service" && orderData.room_number
              ? `Room ${orderData.room_number}`
              : orderData.table_number
              ? `Table ${orderData.table_number}`
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Guest Name">
            {orderData.guest_name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Promotion">
            {orderData.promotion_code || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            {new Date(orderData.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Payment">
            {orderData.payment_method
              ? String(orderData.payment_method).replace("_", " ").toUpperCase()
              : "-"}
          </Descriptions.Item>
          {isRoomService && maxDays > 1 && (
            <>
              <Descriptions.Item label="Duration">
                {maxDays} Days
              </Descriptions.Item>
              <Descriptions.Item label="Valid Until">
                {endDate.toLocaleDateString()}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>

        <Typography.Title level={5}>Order Items</Typography.Title>
        <Table
          dataSource={orderData.items}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
          bordered
        />

        {canViewPrices() && (
          <div style={{ marginTop: 16 }}>
            <Divider style={{ margin: "12px 0" }}>
              Split Bills
            </Divider>
            <Table
              dataSource={orderData.split_bills || []}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              locale={{ emptyText: "No split bills" }}
              columns={[
                { title: "Label", dataIndex: "split_label", key: "split_label" },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  align: "right" as const,
                  render: (v: any) => fmt(v),
                },
                {
                  title: "Method",
                  dataIndex: "payment_method",
                  key: "payment_method",
                  render: (v: any) => v || "-",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (v: any) => (
                    <Tag color={v === "paid" ? "green" : "orange"}>
                      {String(v || "").toUpperCase()}
                    </Tag>
                  ),
                },
                {
                  title: "Action",
                  key: "action",
                  align: "center" as const,
                  render: (_: any, rec: any) =>
                    rec.status !== "paid" ? (
                      <Button
                        size="small"
                        type="primary"
                        loading={splitLoading}
                        onClick={() => handleSettleSplit(rec.id)}
                      >
                        Settle
                      </Button>
                    ) : (
                      <Text type="secondary">
                        {rec.paid_at ? new Date(rec.paid_at).toLocaleString() : "Paid"}
                      </Text>
                    ),
                },
              ]}
            />
            <Space.Compact style={{ width: "100%", marginTop: 8 }}>
              <Input
                placeholder="Split label (e.g. Guest 1)"
                value={splitLabel}
                onChange={(e) => setSplitLabel(e.target.value)}
                style={{ width: 240 }}
              />
              <InputNumber
                placeholder="Amount"
                min={0}
                value={splitAmount}
                onChange={(v) => setSplitAmount(v)}
                style={{ width: 140 }}
              />
              <Select
                value={splitMethod}
                onChange={setSplitMethod}
                options={PAYMENT_METHOD_OPTIONS}
                style={{ width: 150 }}
              />
              <Button type="primary" loading={splitLoading} onClick={handleAddSplit}>
                Add Split
              </Button>
            </Space.Compact>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
              }}
            >
              <Text type="secondary">Split total</Text>
              <Text>
                {fmt(
                  (orderData.split_bills || []).reduce(
                    (s: number, b: any) => s + Number(b.amount || 0),
                    0,
                  ),
                )}
              </Text>
            </div>
          </div>
        )}

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}
        >
          <div
            style={{
              width: 320,
              padding: "0 10px",
            }}
          >
            {canViewPrices() ? (
              <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text type="secondary">Subtotal</Text>
              <Text>
                LKR{" "}
                {Number(orderData.subtotal).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            {Number(orderData.discount_amount) > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <Text type="secondary">Discount</Text>
                <Text type="success">
                  - LKR{" "}
                  {Number(orderData.discount_amount).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Text>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text type="secondary">Service Charge</Text>
              <Text>
                LKR{" "}
                {Number(orderData.service_charge).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <Text type="secondary">Tax / VAT</Text>
              <Text>
                LKR{" "}
                {Number(orderData.tax_amount).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            <Divider style={{ margin: "8px 0" }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong style={{ fontSize: 16 }}>
                Payable Amount
              </Text>
              <Text strong style={{ fontSize: 18, color: "#B79A68" }}>
                LKR{" "}
                {Number(orderData.total_amount).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            </>
            ) : (
              <Text type="secondary" style={{ display: "block", textAlign: "center", padding: "20px 0" }}>
                Prices not available
              </Text>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={paidModalOpen}
        title="Mark Order as Paid"
        okText="Confirm Paid"
        onOk={confirmPaidStatus}
        onCancel={() => setPaidModalOpen(false)}
        width={380}
        centered
      >
        <div style={{ padding: "8px 0" }}>
          <Text type="secondary">Payment Method</Text>
          <Select
            value={paidMethod}
            onChange={setPaidMethod}
            options={PAYMENT_METHOD_OPTIONS}
            style={{ width: "100%", marginTop: 8 }}
          />
        </div>
      </Modal>
    </Modal>
  );
};
